import { Accession, AccessionPATCH, Case, LISCase } from "pwapi";

import { billingTypeEnumToLabel } from "./caseUtils";
import { hasOnlySpecificStrings } from "./formUtils";

/**
 * Function for getting a billing type if it is among the available billing types for a clinic
 * This is primarily used for initializing the case forms from scratch
 *
 * @param initialBillingType initial billing type set on the case
 * @param availableBillingTypes available billing types based on the clinic set on the case
 * @returns The billing type if its among the available billing types, otherwise returns an empty string
 */

// Function for getting a billing type if it is among the available billing types for a clinic
// This is primarily used for initializing the case forms from scratch
export const getBillingTypeFromAvailableTypes = (
  initialBillingType?:
    | LISCase.billing_type
    | Case.billing_type
    | Accession.billing_type,
  availableBillingTypes?: string[]
) => {
  if (
    !initialBillingType ||
    (initialBillingType as LISCase.billing_type | "cash") === "cash"
  )
    return "";

  return availableBillingTypes?.length
    ? availableBillingTypes.includes(initialBillingType)
      ? initialBillingType
      : ""
    : initialBillingType || "";
};

// Common function when attempting to find the initial billing type for billing type select fields after an update
// Will return the initial expected billing type based on the set of information given
export const getUpdatedBillingType = ({
  initialBillingType,
  availableBillingTypes = [],
}: {
  initialBillingType?:
    | LISCase.billing_type
    | Case.billing_type
    | Accession.billing_type;
  availableBillingTypes: string[];
}) => {
  // If there are no available billing types or the initial billing type is one of the available billing types, return the initial billing type.
  if (
    getBillingTypeFromAvailableTypes(initialBillingType, availableBillingTypes)
  )
    return initialBillingType;

  // Constants to avoid running the includes method over and over again for the next set of checks.
  const includesClient = availableBillingTypes.includes(
    LISCase.billing_type.CLIENT
  );
  const includesGlobal = availableBillingTypes.includes(
    LISCase.billing_type.GLOBAL
  );

  // If there is no third-party option but there is a client-bill option, return the client-bill type.
  if (includesClient && !includesGlobal) return LISCase.billing_type.CLIENT;

  // If there is no client-bill but there is a third-party option, return the third-party type.
  if (includesGlobal && !includesClient) return LISCase.billing_type.GLOBAL;

  // There is no initial billing type, the initial billing type isn't among the available types,
  //   the available types include the third-party and client bill or neither one,
  //   and there isn't a properly configured default, return a blank string.
  return "";
};

export const getInitialAccessionBillingType = (value: any, clinics: any) => {
  const billingTypes = (value.id && clinics[value.id]?.billing_types) || [];

  if (billingTypes.length === 1) {
    return billingTypes[0];
  }

  if (
    hasOnlySpecificStrings(
      billingTypes,
      AccessionPATCH.billing_type.CLIENT,
      AccessionPATCH.billing_type.NONE
    )
  )
    return AccessionPATCH.billing_type.CLIENT;

  if (
    hasOnlySpecificStrings(
      billingTypes,
      AccessionPATCH.billing_type.GLOBAL,
      AccessionPATCH.billing_type.NONE
    )
  )
    return AccessionPATCH.billing_type.GLOBAL;

  return null;
};

/**
 * Function which checks the initial billing type of a case, used for checking on e-order billing types in the LIS.
 *
 * @returns {LISCase.billing_type || ''} returns the expected initial billing type or an empty string to represent no billing type
 **/

export const getInitialBillingType = (
  caseBillingType?: LISCase.billing_type,
  availableBillingTypes?: string[]
): LISCase.billing_type | "" => {
  // If there is no set billing type on the case, simply return
  /* TODO: When API chaining is more feasible, instead set this to the default if available.
      Without API chaining, setting to the default does not guarantee a backend update and will misrepresent the value to the user. */
  if (
    !caseBillingType ||
    (caseBillingType as LISCase.billing_type | "cash") === "cash"
  )
    return "";

  // If clinic billing types includes both the third-party and client-bill billing types, return a blank billing type
  if (
    availableBillingTypes?.includes(LISCase.billing_type.GLOBAL) &&
    availableBillingTypes?.includes(LISCase.billing_type.CLIENT)
  )
    return "";

  // If the currently selected type is among the available options, return it
  return getBillingTypeFromAvailableTypes(
    caseBillingType,
    availableBillingTypes
  );
};

/**
 * A function that returns the first available option as per the billing_type enum which is reduced by
 * by the available billing types list.
 *
 * If there are no billing types after the reduction, returns the third-party billing type.
 */

export const getFirstBillingType = (availableBillingTypes: string[]) => {
  const keys = Object.keys(Case.billing_type).filter((k) =>
    availableBillingTypes.includes(
      Case.billing_type[k as keyof typeof Case.billing_type]
    )
  );

  return keys.length > 0
    ? Case.billing_type[keys[0] as keyof typeof Case.billing_type]
    : Case.billing_type.GLOBAL;
};

/**
 * A function which returns the list of available billings types filtered by the available billing types.
 *
 * If there are no available billing types then returns a complete list of billing types.
 */
export const getBillingTypeOptions = (
  availableBillingTypes: string[]
): { label: string; value: string }[] => {
  const billing_types = Object.keys(Case.billing_type).filter(
    (b) =>
      (Case.billing_type[b as keyof typeof Case.billing_type] as
        | LISCase.billing_type
        | "cash") !== "cash"
  );
  const keys = billing_types.filter((k) =>
    availableBillingTypes.includes(
      Case.billing_type[k as keyof typeof Case.billing_type]
    )
  );

  // Restructure the options into a label value pair to keep everything consistent between all the billing type options list
  // If the flag isn't available or there are no available options, return a full list of the billing types
  return (keys.length ? keys : billing_types).map((key) => ({
    label: billingTypeEnumToLabel(
      Case.billing_type[key as keyof typeof Case.billing_type]
    ),
    value: Case.billing_type[key as keyof typeof Case.billing_type],
  }));
};
